import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Backdrop, Box, Button, Checkbox, CircularProgress, Dialog, DialogContent, Divider, FormControlLabel, IconButton, Tab, Tabs, Typography, useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Stories } from '../../store/slices';
import { STORY_CDN, STORY_RENDITIONS } from '../../lib/storyOptions';

type Props = {
    open: boolean;
    onClose: () => void;
    story: any;
    STORY_TAGS: any[];
    STORY_CATEGORIES: any[];
    onPrev: () => void;
    onNext: () => void;
};

/**
 * Tag assignment popup: the video on the left, the tag checkboxes grouped by category on the right,
 * prev/next arrows on the sides. The layout is the one moderators are used to; only the styling changed.
 */
export default function StoryTagsPopup({ open, onClose, story, STORY_TAGS, STORY_CATEGORIES, onPrev, onNext }: Props) {
    const theme = useTheme();
    const [checkedTagIds, setCheckedTagIds] = useState<Set<number>>(new Set());
    const [initialTagIds, setInitialTagIds] = useState<Set<number>>(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [activeVideo, setActiveVideo] = useState<'primary' | 'secondary' | 'production'>('primary');
    const dispatch = useDispatch();

    useEffect(() => {
        const tagIds: Set<number> = new Set<number>((story?.tags || []).map((t: any) => t.tagId));
        setCheckedTagIds(tagIds);
        setInitialTagIds(tagIds);
        setActiveVideo('primary');
    }, [story]);

    const handleVideoChange = (_event: React.SyntheticEvent, newValue: 'primary' | 'secondary' | 'production') => {
        setActiveVideo(newValue);
    };

    const getVideoId = (link: string) => {
        if (!link) return '';
        const parts = link.split('/');
        return parts.length > 1 ? parts[1] : link;
    };

    const currentVideoLink = (() => {
        switch (activeVideo) {
            case 'secondary': return story?.videoLinkSecondary;
            case 'production': return story?.videoLinkProduction;
            default: return story?.videoLink;
        }
    })();
    const currentVideoId = getVideoId(currentVideoLink);

    const groupedTags = STORY_TAGS.reduce((acc, tag) => {
        const category = tag.categoryName || tag.category || 'Uncategorized';
        if (!acc[category]) acc[category] = { tags: [], categoryId: tag.categoryId || 0 };
        acc[category].tags.push(tag);
        return acc;
    }, {} as Record<string, { tags: any[]; categoryId: number }>);

    // Category order follows STORY_CATEGORIES (the backend order), as before.
    const categoryOrderMap = STORY_CATEGORIES.reduce((acc, cat, index) => {
        acc[cat.name || cat.categoryName || 'Uncategorized'] = index;
        return acc;
    }, {} as Record<string, number>);

    const sortedGroupedTags = (Object.entries(groupedTags) as [string, { tags: any[]; categoryId: number }][])
        .sort((a, b) => (categoryOrderMap[a[0]] ?? Number.MAX_SAFE_INTEGER) - (categoryOrderMap[b[0]] ?? Number.MAX_SAFE_INTEGER))
        .map(([category, data]) => [category, data.tags] as [string, any[]]);

    const isChanged = () => {
        if (checkedTagIds.size !== initialTagIds.size) return true;
        let changed = false;
        checkedTagIds.forEach((id) => {
            if (!initialTagIds.has(id)) changed = true;
        });
        return changed;
    };

    const handleCheckboxChange = (tagId: number) => {
        if (isSaving) return;
        setCheckedTagIds((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(tagId)) newSet.delete(tagId);
            else newSet.add(tagId);
            return newSet;
        });
    };

    const handleSave = () => {
        if (isSaving) return;
        setIsSaving(true);
        dispatch<any>(Stories.updateStoryTags({ id: story.storyId, formData: { tagIds: Array.from(checkedTagIds) } })).then((e) => {
            if (e?.error && e.error.message) {
                setIsSaving(false);
            } else {
                setInitialTagIds(new Set(checkedTagIds));
                setIsSaving(false);
            }
        });
    };

    const { color, radius } = theme.custom;
    const navButtonSx = {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        zIndex: 3,
        bgcolor: color.surfaceRaised,
        border: `1px solid ${color.border}`,
        boxShadow: theme.custom.shadow.lg,
        '&:hover': { bgcolor: color.surfaceRaised, borderColor: color.primary.main },
    } as const;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth aria-label={`Tags of story ${story?.storyId ?? ''}`}>
            <DialogContent sx={{ display: 'flex', gap: 3, p: 3, position: 'relative', height: '90vh', overflow: 'hidden' }}>
                <IconButton onClick={onPrev} sx={{ ...navButtonSx, left: 8 }} disabled={isSaving} aria-label="Previous story">
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={onNext} sx={{ ...navButtonSx, right: 8 }} disabled={isSaving} aria-label="Next story">
                    <ArrowForwardIosIcon fontSize="small" />
                </IconButton>

                {/* Video */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', minWidth: 0 }}>
                    {(story?.videoLinkSecondary || story?.videoLinkProduction) && (
                        <Tabs value={activeVideo} onChange={handleVideoChange} centered sx={{ mb: 2, minHeight: 44, flexShrink: 0 }}>
                            <Tab label="Primary" value="primary" />
                            {story?.videoLinkSecondary && <Tab label="Secondary" value="secondary" />}
                            {story?.videoLinkProduction && <Tab label="Production" value="production" />}
                        </Tabs>
                    )}
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', width: '100%', bgcolor: '#000', borderRadius: `${radius.md}px` }}>
                        <video key={currentVideoId} controls style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}>
                            {STORY_RENDITIONS.map((resolution) => (
                                <source key={resolution} src={`${STORY_CDN}/${currentVideoId}/play_${resolution}p.mp4`} type="video/mp4" />
                            ))}
                        </video>
                    </Box>
                </Box>

                {/* Tags */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative', minWidth: 0 }}>
                    <Box sx={{ position: 'absolute', top: 0, right: 0, zIndex: 2 }}>
                        <IconButton onClick={onClose} disabled={isSaving} aria-label="Close" size="small">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                    <Typography sx={{ textAlign: 'center', fontWeight: 600, fontSize: 16, mb: 1.5, pr: 5 }}>
                        Story #{story?.storyId}
                        {isChanged() && (
                            <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 600, color: color.tone.warning.onSoft, bgcolor: color.tone.warning.soft, px: 1, py: 0.25, borderRadius: `${radius.xs}px` }}>
                                Unsaved
                            </Typography>
                        )}
                    </Typography>
                    <Box sx={{ overflowY: 'auto', flexGrow: 1, pr: 1 }}>
                        {sortedGroupedTags.map(([category, tags], index, arr) => {
                            const tagList = tags as any[];
                            return (
                                <Box key={category} sx={{ mb: 2 }}>
                                    <Typography sx={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: color.text.secondary, mb: 0.5 }}>
                                        {category}
                                    </Typography>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }, columnGap: 1 }}>
                                        {tagList.map((tag) => (
                                            <FormControlLabel
                                                key={tag.id}
                                                sx={{ mr: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                                                control={<Checkbox size="small" checked={checkedTagIds.has(tag.id)} onChange={() => handleCheckboxChange(tag.id)} disabled={isSaving} />}
                                                label={tag.name}
                                            />
                                        ))}
                                    </Box>
                                    {index < arr.length - 1 && <Divider sx={{ mt: 1, mb: 1 }} />}
                                </Box>
                            );
                        })}
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1, flexShrink: 0 }}>
                        <Button variant="contained" onClick={handleSave} disabled={!isChanged() || isSaving}>
                            Save
                        </Button>
                        <Button variant="outlined" onClick={onClose} disabled={isSaving}>
                            Cancel
                        </Button>
                    </Box>
                </Box>
                <Backdrop open={isSaving} sx={{ position: 'absolute', zIndex: 4, color: '#fff', backgroundColor: color.overlay }}>
                    <CircularProgress color="inherit" />
                </Backdrop>
            </DialogContent>
        </Dialog>
    );
}
